"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { ROUTES } from "@/lib/routes";

type PlanFeature = {
  title: string;
  description: string;
  iconSrc: string;
};

const planFeatures: PlanFeature[] = [
  {
    title: "Market Intelligence",
    description: "Reviews + competitor analysis",
    iconSrc: "/everything/Frame 99 (6).svg",
  },
  {
    title: "Full Listing Creatives",
    description: "Main images, secondary images, A+ content",
    iconSrc: "/everything/Frame 99 (7).svg",
  },
  {
    title: "SERP Simulation",
    description: "See if your image stand out",
    iconSrc: "/everything/Frame 99 (8).svg",
  },
  {
    title: "Built-in A/B Testing",
    description: "Split test with one click",
    iconSrc: "/everything/Frame 99 (9).svg",
  },
  {
    title: "On-Brand Creatives",
    description: "Your tone, colors, fonts",
    iconSrc: "/everything/Frame 99 (10).svg",
  },
  {
    title: "Collaborate",
    description: "Invite your team",
    iconSrc: "/everything/Frame 99 (11).svg",
  },
  {
    title: "Access to Core Templates",
    description: "Split test with one click",
    iconSrc: "/everything/Frame 99 (12).svg",
  },
  {
    title: "Alexa Shopping Optimization",
    description: "Intelligent Alexa Shopping Insights",
    iconSrc: "/everything/Frame 99 (13).svg",
  },
  {
    title: "Public Project",
    description: "Invite your team",
    iconSrc: "/everything/Frame 99 (14).svg",
  },
];

function FeatureCard({ feature }: { feature: PlanFeature }) {
  return (
    <article className="flex min-h-[230px] flex-col items-center justify-center gap-3 rounded-[20px] border border-[#E0EAF4] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFFFFF_100%)] px-6 py-10 text-center shadow-[0_4px_4px_rgba(7,0,107,0.04)] sm:min-h-[262px] sm:justify-start sm:pt-[60px]">
      <Image
        src={feature.iconSrc}
        alt=""
        width={48}
        height={48}
        aria-hidden="true"
        className="h-12 w-12 shrink-0"
      />

      <h3 className="w-full text-[22px] font-semibold leading-[29px] text-[#1B1C30]">
        {feature.title}
      </h3>
      <p className="max-w-full text-base leading-[21px] text-[#4D4D5C]">
        {feature.description}
      </p>
    </article>
  );
}

export default function EveryPlanFeaturesSection() {
  return (
    <section className="flex w-full flex-col items-center justify-center gap-7 bg-white px-5 py-[60px] sm:px-10 lg:px-[100px]">
      <div className="flex w-full max-w-[1240px] flex-col items-center gap-4 text-center">
        <h2 className="w-full text-[32px] font-bold leading-[42px] text-[#1B1C30] sm:text-[40px] sm:leading-[52px]">
          What You Get with Every Plan
        </h2>
        <p className="max-w-[620px] text-base leading-6 text-[#262626] sm:text-[18px]">
          All plans include powerful tools to create, test, and optimize high-converting Amazon creatives.
        </p>
      </div>

      <div className="grid w-full max-w-[1240px] grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {planFeatures.map((feature) => (
          <FeatureCard key={feature.title} feature={feature} />
        ))}
      </div>

      <Link
        href={ROUTES.pricingAnchor}
        className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[#1464FF] px-5 text-sm font-semibold leading-[18px] tracking-[0.43px] text-white transition-colors duration-200 hover:bg-[#0F55DE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1464FF]"
      >
        Start Free
        <ArrowRight aria-hidden="true" className="h-5 w-5" strokeWidth={1.8} />
      </Link>
    </section>
  );
}
